import { useEffect, useMemo, useRef, useState } from "react";
import { useCvDataExperience } from "../locales/cv-data";
import RightPageNavigationMenu from "../common/right-page-nav-menu";
import VisibilityToggles from "../modules/full-cv/visibility-toggles";
import PositionBlock from "../modules/full-cv/position-block";

const CompanyDivider: React.FC<{ name: string }> = ({ name }) => (
  <div className="flex w-full items-center gap-[12px] pb-[16px] pl-[75px] pt-[8px]">
    <div className="h-px flex-1 bg-[#cbd5e1]" />
    <span className="shrink-0 font-['Bitter',sans-serif] text-[13px] font-medium text-[#64748b]">
      {name}
    </span>
    <div className="h-px flex-1 bg-[#cbd5e1]" />
  </div>
);

const FullCVPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>("geropharm-head");
  const leftScrollRef = useRef<HTMLDivElement | null>(null);

  const [showAchievements, setShowAchievements] = useState<boolean>(true);
  const [showResponsibilities, setShowResponsibilities] =
    useState<boolean>(true);
  const [showProjects, setShowProjects] = useState<boolean>(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

  const workExperience = useCvDataExperience();

  const allPositions = useMemo(
    () => workExperience.flatMap((company) => company.positions),
    [],
  );

  const rightNavItems = useMemo(() => {
    return workExperience.map((company) => ({
      id: company.id,
      label: company.name,
      subNavigation: company.positions.map((position) => ({
        id: position.id,
        label: position.title,
      })),
    }));
  }, [workExperience]);

  useEffect(() => {
    const root = leftScrollRef.current;
    if (!root) return;

    const elements = allPositions
      .map((position) => document.getElementById(position.id))
      .filter((el): el is HTMLElement => el !== null);

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              Math.abs(a.boundingClientRect.top) -
              Math.abs(b.boundingClientRect.top),
          );

        if (visibleEntries.length > 0) {
          const currentId = visibleEntries[0].target.id;
          setActiveSection(currentId);
        }
      },
      {
        root,
        threshold: 0,
        rootMargin: "0px 0px -80% 0px",
      },
    );

    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [allPositions]);

  const scrollToSection = (sectionId: string) => {
    const container = leftScrollRef.current;
    const element = document.getElementById(sectionId);

    if (!container || !element) return;

    const containerRect = container.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const top =
      container.scrollTop + (elementRect.top - containerRect.top) - 20;

    container.scrollTo({
      top,
      behavior: "smooth",
    });
  };

  return (
    <div
      className="
    relative
    left-[20px]
    w-[calc(100vw-40px)] 
    max-w-[750px]
    flex h-full min-h-0 overflow-hidden bg-white
    md:left-[120px]
    md:w-[750px]
  "
    >
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <div
            ref={leftScrollRef}
            className="h-full overflow-y-auto overflow-x-hidden px-[10px] pb-[150px] pt-[20px]"
          >
            <div className="flex w-full max-w-full md:max-w-[550px] flex-col">
              {workExperience.map((company) => (
                <div key={company.id} className="flex flex-col">
                  <CompanyDivider name={company.name} />
                  {company.positions.map((position) => {
                    const globalIndex = allPositions.findIndex(
                      (p) => p.id === position.id,
                    );

                    return (
                      <PositionBlock
                        key={position.id}
                        position={position}
                        nextPosition={allPositions[globalIndex + 1]}
                        showAchievements={showAchievements}
                        showResponsibilities={showResponsibilities}
                        showProjects={showProjects}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="pointer-events-none absolute left-0 right-0 top-0 h-[32px] bg-gradient-to-b from-white to-transparent" />
        </div>
      </div>

      {/* Mobile Menu Button - Visible only on mobile */}
      <button
        onClick={() => setIsMobileMenuOpen(true)}
        className="md:hidden fixed bottom-[20px] right-[20px] w-[56px] h-[56px] bg-[#334155] rounded-full shadow-lg flex items-center justify-center z-40 hover:bg-[#475569] transition-colors"
        aria-label="Open menu"
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
      </button>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sliding Menu */}
      <div
        className={`md:hidden fixed top-0 right-0 h-full w-[280px] bg-white shadow-xl z-50 transition-transform duration-300 ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full relative">
          {/* Close Button */}
          <div className="flex justify-end p-[20px]">
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="absolute top-[15px] right-[15px] w-[40px] h-[40px] flex items-center justify-center hover:bg-[#f1f5f9] rounded-full transition-colors z-10"
              aria-label="Close menu"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                stroke="#334155"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="15" y1="5" x2="5" y2="15" />
                <line x1="5" y1="5" x2="15" y2="15" />
              </svg>
            </button>
          </div>

          {/* Menu Content */}
          <div className="flex-1 overflow-y-auto px-[20px] pb-[20px]">
            <RightPageNavigationMenu
              navItems={rightNavItems}
              activeSection={activeSection}
              scrollToSection={scrollToSection}
              onItemClick={() => setIsMobileMenuOpen(false)}
            >
              <VisibilityToggles
                showAchievements={showAchievements}
                setShowAchievements={setShowAchievements}
                showResponsibilities={showResponsibilities}
                setShowResponsibilities={setShowResponsibilities}
                showProjects={showProjects}
                setShowProjects={setShowProjects}
              />
            </RightPageNavigationMenu>
          </div>
        </div>
      </div>

      {/* Right column */}
      <aside className="max-w-[200px] shrink-0 flex-1 px-[10px] py-[20px] pr-[20px] hidden md:block">
        <RightPageNavigationMenu
          navItems={rightNavItems}
          activeSection={activeSection}
          scrollToSection={scrollToSection}
          onItemClick={() => setIsMobileMenuOpen(false)}
        >
          <VisibilityToggles
            showAchievements={showAchievements}
            setShowAchievements={setShowAchievements}
            showResponsibilities={showResponsibilities}
            setShowResponsibilities={setShowResponsibilities}
            showProjects={showProjects}
            setShowProjects={setShowProjects}
          />
        </RightPageNavigationMenu>
      </aside>
    </div>
  );
};

export default FullCVPage;
